import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { runtimeConfig } from "@src/runtime-config";
import {
  ActiveServer,
  PreassignedServerWithoutGenericOAuth,
  RegisterableServer,
  ServerWithPreassignedStatus,
  ServerWithUnreachableStatus,
  ServerWithUserFoundStatus,
  ServerWithUserNotFoundStatus,
} from "@custom-types/server-types";

/** Order in which we want to display the servers */
const serverOrder: {
  [key in FaroCoreApiTypes.ServerIdentifier]: number;
} = {
  ["sphere_2_com"]: 0,
  ["sphere_2_eu"]: 1,
};

/** Returns the servers sorted according to the defined server order */
export function sortServers(servers: ActiveServer[]): ActiveServer[] {
  return servers.sort(
    (a, b) => serverOrder[a.identifier] - serverOrder[b.identifier]
  );
}

interface GetUrlBasedOnServerReturn {
  /** Url of the API */
  apiBaseUrl: string;

  /** Url of the Sphere Dashboard */
  dashboard2Url: string;

  /** Url of the Entry Page */
  sphereEntryPageUrl: string;
}

/** Returns the appropriate url for the provided server identifier */
export function getUrlBasedOnServer(
  serverIdentifier: FaroCoreApiTypes.ServerIdentifier
): GetUrlBasedOnServerReturn {
  switch (serverIdentifier) {
    case "sphere_2_com":
      return {
        apiBaseUrl: runtimeConfig.urls.apiBaseComUrl,
        dashboard2Url: runtimeConfig.urls.dashboard20ComUrl,
        sphereEntryPageUrl: runtimeConfig.urls.sphereEntryPageComUrl,
      };

    case "sphere_2_eu":
      return {
        apiBaseUrl: runtimeConfig.urls.apiBaseEuUrl,
        dashboard2Url: runtimeConfig.urls.dashboard20EuUrl,
        sphereEntryPageUrl: runtimeConfig.urls.sphereEntryPageEuUrl,
      };

    default:
      throw new Error("Unrecognized server identifier");
  }
}

/**
 * @returns Whether the server entity has "user_found" status and loginProvider defined
 * @param server Server entity to check
 */
export function isServerWithUserFoundStatus(
  server: FaroCoreApiTypes.Server
): server is ServerWithUserFoundStatus {
  return (
    server.status === "user_found" &&
    server.loginProvider !== undefined &&
    server.errorMessage === undefined
  );
}

/**
 * @returns Whether the server entity has "preassigned" status and loginProvider defined
 * @param server Server entity to check
 */
export function isServerWithPreassignedStatus(
  server: FaroCoreApiTypes.Server
): server is ServerWithPreassignedStatus {
  return (
    server.status === "preassigned" &&
    server.loginProvider !== undefined &&
    server.errorMessage === undefined
  );
}

/**
 * @returns Whether the server entity has "user_not_found" status
 * @param server Server entity to check
 */
export function isServerWithUserNotFoundStatus(
  server: FaroCoreApiTypes.Server
): server is ServerWithUserNotFoundStatus {
  return (
    server.status === "user_not_found" &&
    server.loginProvider === undefined &&
    server.errorMessage === undefined
  );
}

/**
 * @returns Whether the server entity has "unreachable" status and errorMessage defined
 * @param server Server entity to check
 */
export function isServerWithUnreachableStatus(
  server: FaroCoreApiTypes.Server
): server is ServerWithUnreachableStatus {
  return (
    server.status === "unreachable" &&
    server.loginProvider === undefined &&
    server.errorMessage !== undefined
  );
}

/**
 * @returns Whether the server entity has "preassigned" status and loginProvider defined
 * and the loginProvider is not "GENERIC_OAUTH"
 * @param server Server entity to check
 */
export function isPreassignedServerWithoutGenericOAuth(
  server: FaroCoreApiTypes.Server
): server is PreassignedServerWithoutGenericOAuth {
  return (
    server.status === "preassigned" &&
    server.loginProvider !== undefined &&
    server.loginProvider.identifier !== "GENERIC_OAUTH" &&
    server.errorMessage === undefined
  );
}

/**
 * @returns Whether the server entity is an active server (user_found or preassigned)
 * @param server Server entity to check
 */
export function isActiveServer(
  server: FaroCoreApiTypes.Server
): server is ActiveServer {
  return (
    isServerWithUserFoundStatus(server) || isServerWithPreassignedStatus(server)
  );
}

/**
 * @returns Whether the server entity is registerable.
 * A registerable entity has user_not_found status or preassigned status without GENERIC_OAUTH auth method.
 * @param server Server entity to check
 */
export function isRegisterableServer(
  server: FaroCoreApiTypes.Server
): server is RegisterableServer {
  return (
    isServerWithUserNotFoundStatus(server) ||
    isPreassignedServerWithoutGenericOAuth(server)
  );
}
